import { Message } from "@mui/icons-material";
import { Box, Button, InputAdornment, Stack, TextField } from "@mui/material";
import dayjs from "dayjs";
import { KeyboardEventHandler, useState } from "react";
import { ConfirmationDialog } from "../../../App/Components/ConfirmationDialog/ConfirmationDialog";
import {
  useMessageInputHandlers,
  useMessageInputState,
} from "../../../App/State/Hooks/MessageDialog/useMessageInput";
import { useDebouncedEffect } from "../../../App/State/Hooks/useDebouncedEffect";
import { IMessageScheduleQueue } from "../../../App/Typings/MessageText";
import { TextMessageErrorMessaging } from "../../SendGroupMessage/Components/SharedComponents/TextMessageInput";
import { ToggleNotificationAlerts } from "./ToggleNotificationAlerts";

export default function SendNowFooter({
  disableInput,
  disableSendButton,
  disableNotificationToggle = false,
  handleSendMessages,
  isValidTime,
  notifyMe = false,
  setNotifyMe,
  RegulationTextComponent,
  QuotaUsageComponent,
  customErrorMessage,
}: {
  disableInput: boolean;
  disableSendButton: boolean;
  disableNotificationToggle?: boolean;
  handleSendMessages: (msgList: IMessageScheduleQueue[]) => void;
  isValidTime: boolean;
  notifyMe?: boolean;
  setNotifyMe?: (value: boolean) => void;
  RegulationTextComponent?: JSX.Element;
  QuotaUsageComponent?: JSX.Element;
  customErrorMessage?: string;
}) {
  const [messageScheduleDialogOpen, setMessageScheduleDialogOpen] = useState(false);
  const { body, subject, bodyErrorMessage, textContainsLink, textSplitCount, textSplitWarning } =
    useMessageInputState();
  const { handleClearInputState, handleInputTextChange } = useMessageInputHandlers();

  const tryToSendMessage = (msgList: IMessageScheduleQueue[]) => {
    if (!isValidTime) {
      setMessageScheduleDialogOpen(true);
    } else {
      sendMessage(msgList);
    }
  };

  const sendMessage = (msgList: IMessageScheduleQueue[]) => {
    handleSendMessages(msgList);
    handleClearInputState();
  };

  const handleSendConfirm = () => {
    const now = dayjs().utc();
    const newMsg: IMessageScheduleQueue = {
      id: now.toISOString(),
      date: now,
      title: subject || "",
      bodyRaw: body,
      scheduled: true,
    };
    sendMessage([newMsg]);
    setMessageScheduleDialogOpen(false);
  };

  const handleSendNow = () => {
    const now = dayjs().utc();

    const newMsg: IMessageScheduleQueue = {
      id: now.toISOString(),
      date: now,
      title: subject || "",
      bodyRaw: body,
      scheduled: !isValidTime,
    };
    tryToSendMessage([newMsg]);
  };

  return (
    <Box sx={{ width: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 1 }}>
        <InputTextMessage
          onChange={handleInputTextChange}
          defaultValue={body}
          disabled={disableInput}
          disabledSend={disableSendButton}
          handleSendNow={handleSendNow}
          errorMessage={customErrorMessage ?? bodyErrorMessage}
        />
        <ToggleNotificationAlerts
          notifyMe={notifyMe}
          setNotifyMe={setNotifyMe}
          disableNotificationToggle={disableNotificationToggle}
          isMultiMessage={true}
        />
      </Stack>
      <TextMessageErrorMessaging
        inputTextMessage={body}
        bodyErrorMessage={bodyErrorMessage}
        textSplitCount={textSplitCount}
        textSplitWarning={textSplitWarning}
        textContainsLinkMessage={textContainsLink}
        RegulationTextComponent={RegulationTextComponent}
        customErrorMessage={customErrorMessage}
      />
      {QuotaUsageComponent}
      <ConfirmationDialog
        cancelMessage="Cancel"
        confirmMessage="Confirm"
        handleCancel={() => setMessageScheduleDialogOpen(false)}
        handleClose={() => setMessageScheduleDialogOpen(false)}
        handleConfirm={handleSendConfirm}
        isOpen={messageScheduleDialogOpen}
        message="This message falls outside of the federal TCPA regulations and will be scheduled at the first available time."
        title="Warning"
        buttonsDisabled={false}
      />
    </Box>
  );
}

const InputTextMessage = ({
  onChange,
  defaultValue,
  disabled,
  disabledSend,
  handleSendNow,
  errorMessage,
}: {
  onChange: (value: string) => void;
  defaultValue: string;
  disabled: boolean;
  disabledSend: boolean;
  handleSendNow: () => void;
  errorMessage?: string;
}) => {
  const [inputText, setInputText] = useState(defaultValue);
  const isInputOrTimeError = Boolean(errorMessage);
  const handleInputBodyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(e.currentTarget.value);
  };
  const handleSendWithEnter: KeyboardEventHandler = (e) => {
    const isEnterButton = e.key === "Enter" || e.code === "13";
    if (!(e.shiftKey || e.ctrlKey) && isEnterButton && !disabledSend && !disabled) {
      handleSendNow();
      setInputText("");
    }
  };
  useDebouncedEffect(
    () => {
      onChange(inputText);
    },
    [inputText],
    200,
  );
  return (
    <TextField
      fullWidth
      id="inputSendTextMessage"
      name="inputSendTextMessage"
      placeholder="Send a message"
      onChange={handleInputBodyChange}
      onKeyDown={handleSendWithEnter}
      value={inputText}
      disabled={disabled}
      color={isInputOrTimeError ? "error" : "primary"}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Message />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <Box>
              <Button
                id="send-message-menu-button"
                aria-label="Send Message"
                variant="contained"
                disableElevation
                onClick={disabled || disabledSend ? undefined : handleSendNow}
                disabled={disabledSend}
                color={isInputOrTimeError ? "error" : "primary"}
                sx={{ px: 4 }}
                title={errorMessage}
              >
                Send Message
              </Button>
            </Box>
          </InputAdornment>
        ),
      }}
    />
  );
};
